import { useState, useEffect } from "react";
import { Box, Skeleton, Text, Toast } from "@chakra-ui/react";
import { Line } from "react-chartjs-2";
import useAxios from "../../utils/useAxios";
import { MoistureIndexThresholds } from "../../utils/colorUtils/mapColorUtils";
import { createMoistureIndexShading } from "./moistureIndexChartPlugin";

interface Props {
  deviceId: number;
  isGraphloading: boolean;
  setIsGraphloading: (isLoading: boolean) => void;
  plot_key?: string;
  moistureIndexThresholds?: MoistureIndexThresholds;
}

const MiniMoistureGraph = ({
  deviceId,
  isGraphloading,
  setIsGraphloading,
  plot_key = "soil_moisture",
  moistureIndexThresholds = {},
}: Props) => {
  const api = useAxios();
  const [timestamps, setTimestamps] = useState<string[]>([]);
  const [values, setValues] = useState<Array<number | null>>([]);
  const [isDataUnavailable, setIsDataUnavailable] = useState(false);

  useEffect(() => {
    setIsGraphloading(true);
    setIsDataUnavailable(false);
    const fetchData = async () => {
      if (!deviceId) return;
      try {
        const response = await api.get(`frontend/devices/plottable-data/${deviceId}/`, {
          params: { days: 5, aggregate: "hour" },
        });
        const plotValues = response.data[plot_key];
        const hasPlotData =
          Array.isArray(plotValues) &&
          plotValues.some((value: unknown) => typeof value === "number");
        if (hasPlotData && response.data.timestamp) {
          setValues(plotValues);
          setTimestamps(
            response.data.timestamp.map((ts: string) =>
              new Date(ts).toLocaleString("nb-NO", { month: "numeric", day: "numeric" }),
            ),
          );
        } else {
          setValues([]);
          setTimestamps([]);
          setIsDataUnavailable(true);
        }
      } catch (err) {
        console.error(err);
        Toast({
          title: "Error",
          description: "Failed to fetch data",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsGraphloading(false);
      }
    };
    fetchData();
  }, [deviceId, plot_key]);

  if (isGraphloading) {
    return <Skeleton />;
  }

  const plotLabels: Record<string, string> = {
    soil_moisture: "Soil Moisture",
    soil_moisture_2: "Soil Moisture 2",
    soil_moisture_3: "Soil Moisture 3",
    moisture_index_1: "Moisture Index 1",
    moisture_index_2: "Moisture Index 2",
    moisture_index_3: "Moisture Index 3",
    soil_conductivity: "Soil Conductivity",
    soil_conductivity_2: "Soil Conductivity 2",
    soil_conductivity_3: "Soil Conductivity 3",
    air_temperature: "Air Temperature",
    air_temperature_2: "Air Temperature 2",
    soil_temperature: "Soil Temperature",
  };

  const isMoistureIndex = plot_key.startsWith("moisture_index_");
  const indexBounds = values.reduce(
    (bounds, value) =>
      value !== null && Number.isFinite(value)
        ? { min: Math.min(bounds.min, value), max: Math.max(bounds.max, value) }
        : bounds,
    { min: 0, max: 1 },
  );
  const chartConfig = isMoistureIndex
    ? {
        label: plotLabels[plot_key],
        backgroundColor: "rgba(105,75,180,0.2)",
        borderColor: "rgba(105,75,180,1)",
        suggestedMin: -0.1,
        suggestedMax: 1.1,
      }
    : plot_key === "air_temperature" ||
      plot_key === "air_temperature_2" ||
      plot_key === "soil_temperature"
      ? {
          label: plotLabels[plot_key] || "Temperature",
          backgroundColor: "rgba(250,150,100,0.2)",
          borderColor: "rgba(250,150,100,1)",
          suggestedMin: 0,
          suggestedMax: 50,
        }
      : plot_key === "soil_conductivity" ||
          plot_key === "soil_conductivity_2" ||
          plot_key === "soil_conductivity_3"
        ? {
            label: plotLabels[plot_key] || "Soil Conductivity",
            backgroundColor: "rgba(255,165,0,0.2)",
            borderColor: "rgba(255,165,0,1)",
            suggestedMin: 0,
            suggestedMax: 10,
          }
        : {
            label: plotLabels[plot_key] || "Soil Moisture",
            backgroundColor: "rgba(75,192,192,0.2)",
            borderColor: "rgba(75,192,192,1)",
            suggestedMin: 0,
            suggestedMax: 60,
          };

  if (isDataUnavailable) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center" px={4}>
        <Text textAlign="center" fontSize="sm" color="gray.600">
          {chartConfig.label} is not available for this device.
        </Text>
      </Box>
    );
  }

  return (
    <Box position="relative" width="100%" height="100%" minWidth={0}>
      {/* This Chart.js wrapper installs plugins only when creating the chart. */}
      <Line
        key={
          isMoistureIndex
            ? `${plot_key}:${moistureIndexThresholds.management}:${moistureIndexThresholds.deficit}:${moistureIndexThresholds.upperManagement}`
            : plot_key
        }
        plugins={isMoistureIndex ? [createMoistureIndexShading(moistureIndexThresholds)] : []}
        data={{
          labels: timestamps,
          datasets: [
            {
              label: chartConfig.label,
              data: values,
              fill: !isMoistureIndex,
              backgroundColor: chartConfig.backgroundColor,
              borderColor: chartConfig.borderColor,
              pointRadius: 1,
            },
          ],
        }}
        options={{
          maintainAspectRatio: false,
          ...(isMoistureIndex
            ? {
                layout: { padding: 0 },
                legend: { labels: { boxWidth: 10, fontSize: 10, padding: 4 } },
              }
            : {}),
          scales: {
            ...(isMoistureIndex
              ? {
                  xAxes: [
                    {
                      ticks: { maxTicksLimit: 5, maxRotation: 0, fontSize: 10, padding: 2 },
                      gridLines: { display: false, tickMarkLength: 3 },
                    },
                  ],
                }
              : {}),
            yAxes: [
              {
                ...(isMoistureIndex ? { gridLines: { tickMarkLength: 3 } } : {}),
                ticks: {
                  suggestedMin: chartConfig.suggestedMin,
                  suggestedMax: chartConfig.suggestedMax,
                  ...(isMoistureIndex
                    ? {
                        fontSize: 10,
                        padding: 2,
                        min: indexBounds.min - 0.05,
                        max: indexBounds.max + 0.05,
                        maxTicksLimit: 6,
                        callback: (value: number | string) =>
                          Number(Number(value).toFixed(2)),
                      }
                    : {}),
                },
              },
            ],
          },
        }}
      />
    </Box>
  );
};

export default MiniMoistureGraph;
